// Player-facing presentation and accessibility settings (Phase 10). These are
// deliberately kept apart from GameState: they are about how the game is shown,
// not about the world, so they persist under their own key and survive across
// saves, deaths and new games alike. Everything degrades safely when a browser
// API is missing (the test runner has no `document`), so the module is import-
// safe from Node.

import { useEffect, useState } from "react"

export type TextScale = "small" | "normal" | "large" | "xlarge"
export type MotionLevel = "full" | "reduced" | "none"
/** How long the field pauses on the player's blow before the enemy answers it. */
export type BattlePace = "instant" | "brisk" | "measured"

export interface Settings {
  /** Scales the root font, and with it every length expressed in rem/em. */
  textScale: TextScale
  /** How much of the game's animation to play. "none" stills everything. */
  motion: MotionLevel
  /** Battle pacing — the pause between acting and the enemy's response. */
  battlePace: BattlePace
  /** Auto-advance a conversation the player has already read to its end. */
  autoSkipSeenDialogue: boolean
  /** Ask before an action that will spend many days at once. */
  confirmLongActions: boolean
  /** How many days an action must cost before it is worth confirming. */
  longActionThreshold: number
}

export const DEFAULT_SETTINGS: Settings = {
  textScale: "normal",
  motion: "full",
  battlePace: "brisk",
  autoSkipSeenDialogue: false,
  confirmLongActions: true,
  longActionThreshold: 10,
}

const SETTINGS_KEY = "echoesBeforeEternity.settings"

const TEXT_SCALES: readonly TextScale[] = ["small", "normal", "large", "xlarge"]
const MOTION_LEVELS: readonly MotionLevel[] = ["full", "reduced", "none"]
const BATTLE_PACES: readonly BattlePace[] = ["instant", "brisk", "measured"]

const BATTLE_PACE_MS: Record<BattlePace, number> = {
  instant: 0,
  brisk: 300,
  measured: 700,
}

/** The pause, in ms, the battle screen holds before the enemy answers a blow. */
export function getBattlePaceDelayMs(settings: Settings): number {
  if (isMotionOff(settings)) return 0
  return BATTLE_PACE_MS[settings.battlePace]
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/** True when animation should be trimmed — either by choice or by OS preference. */
export function isMotionReduced(settings: Settings): boolean {
  if (settings.motion === "reduced" || settings.motion === "none") return true
  return prefersReducedMotion()
}

/** True when animation should be stilled entirely. Only the explicit choice does this. */
export function isMotionOff(settings: Settings): boolean {
  return settings.motion === "none"
}

/** Fills in any field a stored blob is missing, and clamps the one number. */
function coerce(raw: unknown): Settings {
  const partial = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<Settings>
  const merged: Settings = { ...DEFAULT_SETTINGS, ...partial }
  if (!TEXT_SCALES.includes(merged.textScale)) merged.textScale = DEFAULT_SETTINGS.textScale
  if (!MOTION_LEVELS.includes(merged.motion)) merged.motion = DEFAULT_SETTINGS.motion
  if (!BATTLE_PACES.includes(merged.battlePace)) merged.battlePace = DEFAULT_SETTINGS.battlePace
  merged.autoSkipSeenDialogue = Boolean(merged.autoSkipSeenDialogue)
  merged.confirmLongActions = Boolean(merged.confirmLongActions)
  const threshold = Number(merged.longActionThreshold)
  merged.longActionThreshold = Number.isFinite(threshold)
    ? Math.min(512, Math.max(1, Math.round(threshold)))
    : DEFAULT_SETTINGS.longActionThreshold
  return merged
}

let cached: Settings | null = null
const listeners = new Set<(settings: Settings) => void>()

export function loadSettings(): Settings {
  if (cached) return cached
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    cached = raw ? coerce(JSON.parse(raw)) : { ...DEFAULT_SETTINGS }
  } catch {
    cached = { ...DEFAULT_SETTINGS }
  }
  return cached
}

export function saveSettings(next: Settings): Settings {
  const settings = coerce(next)
  cached = settings
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
  } catch {
    // A full or disabled localStorage should never crash the game; the change
    // still takes effect in memory for the rest of the session.
  }
  applySettings(settings)
  for (const listener of listeners) listener(settings)
  return settings
}

export function subscribeSettings(listener: (settings: Settings) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Drops the in-memory cache so the next load re-reads storage. Tests only. */
export function resetSettingsCache(): void {
  cached = null
}

/**
 * Stamps the settings the stylesheet reads onto the document root: a text-scale
 * that resizes the root font, and a motion level a single CSS layer uses to
 * still or trim animation everywhere at once. "full" clears the motion flag so
 * the OS `prefers-reduced-motion` media query is left to govern on its own.
 */
export function applySettings(settings: Settings): void {
  if (typeof document === "undefined") return
  const root = document.documentElement
  root.dataset.textScale = settings.textScale
  if (settings.motion === "full") {
    delete root.dataset.motion
  } else {
    root.dataset.motion = settings.motion
  }
}

/**
 * React binding: the current settings, and an updater that patches one or more
 * fields. Every subscriber re-renders when any field changes, so a control on
 * the Settings screen and a battle pausing on the other side of the app always
 * agree.
 */
export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [settings, setSettings] = useState<Settings>(loadSettings)
  useEffect(() => subscribeSettings(setSettings), [])
  const update = (patch: Partial<Settings>) => {
    saveSettings({ ...loadSettings(), ...patch })
  }
  return [settings, update]
}
